import React, { useState, useEffect } from "react";
import "./EditArticle.css";
import Widgets from "../../../widgets";
import strings from "../../../utils/strings";
import { useTranslation } from "react-i18next";
import DatePicker from "react-multi-date-picker";
import Icons from "../../../Assets/Icons";
import { parse, format, isValid } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { uploadMedia } from "../../../modules/patientProfile/patientProfileThunk";
import { resetUploadMedia } from "../../../modules/patientProfile/patientProfileSlice";
import { getArticleDetail, toggleActiveArticle, editArticle } from "../../../modules/article/ArticleThunk";
import { resetArticleDetail, resetToggleArticle, resetEditArticle } from "../../../modules/article/ArticleSlice";
import ToastPopup from "../../../widgets/Toast/toastPopup";
import useValidation from "../../../utils/validators";
import TextArea from "antd/es/input/TextArea";

const EditArticle = ({ setSelectedView, userId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { validateEditArticle } = useValidation();
  const breadcrumbs = ["healthArt", "artDetails"];
  const paths = [{ onClick: () => setSelectedView("artList") }, strings.navigation.healthArticle];
  const selectedLanguage = useSelector((state) => state.language.language);
  const [articleImg, setArticleImg] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [rawDate, setRawDate] = useState(new Date().toISOString());
  const weekDays = ["S", "M", "T", "W", "T", "F", "S"];
  const [action, setAction] = useState("");
  const [lang, setLang] = useState({ value: "en", label: "English" });
  const [sections, setSections] = useState([{ contentType: "Description", content: "", title: "" }]);
  const [formData, setFormData] = useState({
    readingTime: 0,
  });
  const { uploadMediaSuccess, uploadMediaErr } = useSelector((state) => state.patientProfile);
  const { articleDetailSuccess, articleDetailErr, toggleArtErr, toggleArtSuccess, editArtErr, editArtSuccess } = useSelector((state) => state.article);

  const [articleData, setArticleData] = useState({});
  const [currentIndex, setCurrentIndex] = useState(null);
  const [isOn, setIsOn] = useState(false);

  useEffect(() => {
    if (selectedLanguage === "en") {
      setLang({ value: "en", label: "English" });
    } else if (selectedLanguage === "vietnam") {
      setLang({ value: "vi", label: "Vietnamese" });
    }
  }, [selectedLanguage]);
  useEffect(() => {
    if (editArtErr) {
      ToastPopup.error(editArtErr);
    }
    if (editArtSuccess && editArtSuccess.status === "success") {
      setSelectedView("artList");
      ToastPopup.success(t("artUpdated"));
    }
    dispatch(resetEditArticle());
    // console.log(resetEditArticle);
  }, [editArtErr, editArtSuccess]);

  useEffect(() => {
    if (toggleArtErr) {
      ToastPopup.error(toggleArtErr);
    }
    if (toggleArtSuccess && toggleArtSuccess.status === "success") {
      setIsOn(toggleArtSuccess.data.article.isActive);
    }
    dispatch(resetToggleArticle());
  }, [toggleArtSuccess, toggleArtErr]);

  useEffect(() => {
    if (articleDetailErr) {
      ToastPopup.error(articleDetailErr);
    }
    if (articleDetailSuccess && articleDetailSuccess.status === "success") {
      const art = articleDetailSuccess.data.article;
      const normalizedRef =
        art && typeof art.reference === "string"
          ? { en: art.reference, vi: art.reference }
          : art.reference;
      setArticleData({ ...art, reference: normalizedRef });
      setIsOn(articleDetailSuccess.data.article.isActive);
      setRawDate(articleDetailSuccess.data.article.date);
    }
    dispatch(resetArticleDetail());
  }, [articleDetailSuccess, articleDetailErr]);

  useEffect(() => {
    if (uploadMediaErr) {
      ToastPopup.error(uploadMediaErr);
    }
    if (uploadMediaSuccess && uploadMediaSuccess.status === "success") {
      const url = uploadMediaSuccess.data.urls[0];
      if (action === "articleImg") {
        setArticleData((prevData) => ({
          ...prevData,
          coverImage: url,
        }));
      }
    }
    dispatch(resetUploadMedia());
  }, [uploadMediaErr, uploadMediaSuccess]);

  useEffect(() => {
    if (userId) {
      dispatch(getArticleDetail({ userId }));
    }
  }, [userId]);

  const handleNameChange = (e) => {
    const { name, value } = e.target;

    if (name === "author" && !/^[A-Za-z\s]*$/.test(value)) return;

    const langKey = lang?.value;

    setArticleData((prevArticleData) => {
      // Handle direct properties like 'reference' and 'websiteName'
      if (["author","websiteName"].includes(name)) {
        return {
          ...prevArticleData,
          [name]: value.trimStart(),
        };
      }
      if (name === "reference") {
        return {
          ...prevArticleData,
          reference: {
            ...(prevArticleData.reference || {}),
            [langKey]: value.trimStart(),
          },
        };
      }

      // Handle nested properties when langKey exists
      return {
        ...prevArticleData,
        [langKey]: {
          ...(prevArticleData[langKey] || {}), // Ensure it exists
          [name]: value.trimStart(),
        },
      };
    });
  };

  const getDateYearsBefore = (years) => {
    const today = new Date();
    return new Date(today.getFullYear() - years, today.getMonth(), today.getDate());
  };
  const getFormattedDate = (date, formatPattern = "dd/MM/yyyy") => {
    if (!date || isNaN(new Date(date).getTime())) {
      return "";
    }
    const dateObj = new Date(date);
    return format(dateObj, formatPattern);
  };

  const handleImageUpload =
    (idType) =>
    ({ files }) => {
      dispatch(uploadMedia({ files }));
      setAction(idType);
    };

  const handleInputChange = (e, index, field = "content") => {
    const value = e.target.value;
    const langKey = lang.value;
    setArticleData((prevArticleData) => {
      let updatedSections = [...prevArticleData[langKey].additionalContent.sections];
      let updatedSection = { ...updatedSections[index] };
      if (updatedSection.contentType === "Description" || field === "description") {
        updatedSection.description = value;
      } else if (field === "content") {
        updatedSection.content = value.split("\n");
      }

      updatedSections[index] = updatedSection;
      return {
        ...prevArticleData,
        [langKey]: {
          ...prevArticleData[langKey],
          additionalContent: {
            ...prevArticleData[langKey].additionalContent,
            sections: updatedSections,
          },
        },
      };
    });
  };

  const handleAddSection = () => {
    const enSections = articleData?.en?.additionalContent?.sections || [];
    const viSections = articleData?.vi?.additionalContent?.sections || [];
    const lastEn = enSections[enSections.length - 1];
    const lastVi = viSections[viSections.length - 1];
    console.log(lastEn);
    if ((!lastEn || (lastEn.content && lastEn.content.length > 1) || lastEn.description) && (!lastVi || (lastVi.content && lastVi.content.length > 1) || lastVi.description)) {
      const newSection = {
        title: "",
        description: "",
        isAdded: true,
      };

      const updatedEnSections = [...enSections, newSection];
      const updatedViSections = [...viSections, newSection];

      setArticleData((prevState) => ({
        ...prevState,
        en: {
          ...prevState.en,
          additionalContent: {
            ...prevState.en.additionalContent,
            sections: updatedEnSections,
          },
        },
        vi: {
          ...prevState.vi,
          additionalContent: {
            ...prevState.vi.additionalContent,
            sections: updatedViSections,
          },
        },
      }));
    } else if (lastEn.description === "") {
      ToastPopup.error(t("previousSectionErrEn"));
    } else if (lastVi.description === "") {
      ToastPopup.error(t("previousSectionErrVi"));
    } else if (lastEn.content.length < 2) {
      ToastPopup.error(t("previousListErrEn"));
    } else if (lastVi.content.length < 2) {
      ToastPopup.error(t("previousListErrVi"));
    } else {
      ToastPopup.error(t("plsFillPrevSecEnVi"));
    }
  };

  const handleDropdownChange = (value, index) => {
    setArticleData((prevData) => {
      const updatedEnSections = [...prevData.en.additionalContent.sections];
      const updatedViSections = [...prevData.vi.additionalContent.sections];
      if (value.value === "unorderedListItem") {
        if (updatedEnSections[index]) {
          delete updatedEnSections[index].description;
          updatedEnSections[index].content = updatedEnSections[index].content || [""];
        }
        if (updatedViSections[index]) {
          delete updatedViSections[index].description;
          updatedViSections[index].content = updatedViSections[index].content || [""];
        }
      } else if (value.value === "description") {
        if (updatedEnSections[index]) {
          delete updatedEnSections[index].content;
          updatedEnSections[index].description = "";
        }
        if (updatedViSections[index]) {
          delete updatedViSections[index].content;
          updatedViSections[index].description = "";
        }
      }
      const newCategory = value.value === "unorderedListItem" ? "List Item Category" : "Description Category";

      return {
        ...prevData,
        en: {
          ...prevData.en,
          additionalContent: {
            ...prevData.en.additionalContent,
            sections: updatedEnSections,
          },
        },
        vi: {
          ...prevData.vi,
          additionalContent: {
            ...prevData.vi.additionalContent,
            sections: updatedViSections,
          },
        },
        category: newCategory,
      };
    });
  };

  const handleSave = () => {
    const isValid = validateEditArticle(articleData, lang.value);
    const totalMinutes = Number(formData.readingTime);
    const updatedInfo = {
      author: articleData.author,
      coverImage: articleData.coverImage.replace(/ /g, "%20"),
      date: articleData.date,
      websiteName: articleData.websiteName,
      reference: articleData.reference,
      readingTime: {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
      },
      en: articleData.en,
      vi: articleData.vi,
    };
    if (isValid) {
      dispatch(editArticle({ userId, updatedInfo }));
    }
  };

  const handleCancel = () => {
    setSelectedView("artList");
  };

  const handleToggle = () => {
    dispatch(toggleActiveArticle({ userId }));
  };

  const handleImgDel = (imageKey) => (uploadedImage) => {
    setArticleData((prevData) => ({
      ...prevData,
      [imageKey]: uploadedImage,
    }));
  };

  useEffect(() => {
    if (articleData?.readingTime) {
      const { hours, minutes } = articleData.readingTime;
      const totalMinutes = hours * 60 + minutes;
      setFormData((prevData) => ({
        ...prevData,
        readingTime: totalMinutes,
      }));
    }
  }, [articleData]);

  const handleContentDelete = (index) => {
    setCurrentIndex(index);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (currentIndex !== null) {
      setArticleData((prevData) => {
        const updatedEnSections = [...prevData.en.additionalContent.sections];
        updatedEnSections.splice(currentIndex, 1);
        const updatedViSections = [...prevData.vi.additionalContent.sections];
        updatedViSections.splice(currentIndex, 1);
        return {
          ...prevData,
          en: {
            ...prevData.en,
            additionalContent: {
              ...prevData.en.additionalContent,
              sections: updatedEnSections,
            },
          },
          vi: {
            ...prevData.vi,
            additionalContent: {
              ...prevData.vi.additionalContent,
              sections: updatedViSections,
            },
          },
        };
      });
      setCurrentIndex(null);
    }
    setShowConfirmModal(false);
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setCurrentIndex(null);
  };

  useEffect(() => {
    if (rawDate) {
      const normalizeDate = (rawDate) => new Date(rawDate).toISOString();
      let newDate = normalizeDate(rawDate);
      setArticleData((prevData) => ({
        ...prevData,
        date: newDate,
      }));
    }
  }, [rawDate]);

  const handleReadingTimeChange = (e) => {
    const totalMinutes = parseInt(e.target.value, 10) || 0;
    setArticleData((prevData) => ({
      ...prevData,
      readingTime: {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
      },
    }));
    setFormData((prevData) => ({
      ...prevData,
      readingTime: totalMinutes,
    }));
  };
  const options = [
    { value: "en", label: "English" },
    { value: "vi", label: "Vietnamese" },
  ];
  const listType = [
    { value: "description", label: t("description") },
    { value: "unorderedListItem", label: t("unorderedListItem") },
  ];
  console.log(articleData?.vi?.additionalContent.sections);
  console.log(articleData?.en?.additionalContent.sections);

  return (
    <div className="department-box article-box">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="department-heading-row">
        <div className="department-heading"> {lang.value === "en" ? t("artDetailsEn") : t("artDetailsVi")} </div>
        <Widgets.NewSelectDropdown options={options} big placeholder={t("selGrpName")} onChange={(i) => setLang(i)} selectedValue={lang} />
      </div>
      <div className="article-head">
        <div className="art-switch">
          <div className="art-form">{t("artFrom")}</div>
          <div className="publish-toggle-container">
            {isOn ? <div className="article-publish-label">{t("published")}</div> : <div className="article-publish-label">{t("unpublished")}</div>}
            <div className="art-toggle">
              <Widgets.ToggleSwitch isOn={isOn} handleToggle={handleToggle} />
            </div>
          </div>
        </div>
        <div class="art-form-container">
          <div class="art-title art">
            <label className="art-label">
              {t("title")} {lang.value === "en" ? <>(En)</> : <>(Vi)</>} <span className="astrik">*</span>
            </label>
            {lang.value === "en" ? (
              <input value={articleData?.en?.title} type="text" className="art-input" placeholder={t("enterMainTitle")} name="title" maxLength={100} onChange={handleNameChange} />
            ) : (
              <input value={articleData?.vi?.title} type="text" className="art-input" placeholder={t("enterMainTitle")} name="title" maxLength={100} onChange={handleNameChange} />
            )}
          </div>
          <div class="art-content-category art seprate-textarea">
            <label className="art-label">
              {t("content")} {lang.value === "en" ? <>(En)</> : <>(Vi)</>} <span className="astrik">*</span>
            </label>
            {lang.value === "en" ? (
              <TextArea value={articleData?.en?.content} maxLength={500} className="art-input text-area-form" placeholder={t("enterContentofArt")} name="content" onChange={handleNameChange} />
            ) : (
              <TextArea value={articleData?.vi?.content} maxLength={500} className="art-input text-area-form" placeholder={t("enterContentofArt")} name="content" onChange={handleNameChange} />
            )}
          </div>
          <div class="art-category art">
            <label className="art-label">
              {t("category")} {lang.value === "en" ? <>(En)</> : <>(Vi)</>} <span className="astrik">*</span>
            </label>
            {lang.value === "en" ? (
              <input value={articleData?.en?.category} type="text" className="art-input" placeholder={t("enterCategory")} name="category" onChange={handleNameChange} />
            ) : (
              <input value={articleData?.vi?.category} type="text" className="art-input" placeholder={t("enterCategory")} name="category" onChange={handleNameChange} />
            )}
          </div>
          <div class="art-date art">
            <label className="art-label">
              {t("dateOfPublish")} <span className="astrik">*</span>
            </label>
            <section className="pdl-detail date-pick">
              <input className="art-input" type="text" value={getFormattedDate(articleData.date, "dd/MM/yyyy")} />
              <img src={Icons.blackCalender} alt="calender" className="art-date-icon" />
              <section className="custom-date-picker">
                <DatePicker
                  value={rawDate}
                  onChange={(date) => setRawDate(date)}
                  format="DD/MM/YYYY"
                  editable={false}
                  weekDays={weekDays}
                  minDate={getDateYearsBefore(10)}
                  maxDate={getDateYearsBefore(0)}
                />
              </section>
            </section>
          </div>
          <div class="art-author-name art">
            <label className="art-label">
              {t("authorName")} (En) <span className="astrik">*</span>
            </label>
            <input value={articleData.author} type="text" className="art-input" placeholder={t("enterAuthor")} name="author" maxLength={100} onChange={handleNameChange} />
          </div>
          <div class="art-reading-time art">
            <label className="art-label">
              {t("readInMin")} <span className="astrik">*</span>
            </label>
            <input
              value={formData.readingTime}
              type="text"
              className="art-input"
              placeholder="Enter total reading time in minutes"
              onChange={handleReadingTimeChange}
              name="readingTime"
              maxLength={2}
            />
          </div>
          {/* English Reference Field */}
          {lang.value === "en" && (
            <div class="art-reference art">
              <label className="art-label">
                Reference (En) <span className="astrik">*</span>
              </label>
              <input
                value={articleData?.reference?.en || ""}
                type="text"
                className="art-input"
                placeholder={t("enterRef")}
                onChange={handleNameChange}
                name="reference"
              />
            </div>
          )}
          
          {/* Vietnamese Reference Field */}
          {lang.value === "vi" && (
            <div class="art-reference art">
              <label className="art-label">
                Tham Khảo (Vi) <span className="astrik">*</span>
              </label>
              <input
                value={articleData?.reference?.vi || ""}
                type="text"
                className="art-input"
                placeholder={t("enterRef")}
                onChange={handleNameChange}
                name="reference"
              />
            </div>
          )}
          <div class="art-website-name art">
            <label className="art-label">
              {t("webName")} (En) <span className="astrik">*</span>
            </label>
            <input value={articleData?.websiteName || ""} type="text" maxLength={100} className="art-input" placeholder={t("enterWeb")} name="websiteName" onChange={handleNameChange} />
          </div>
          <div>
            <label className="art-label-green">
              {t("uploadArtPic")} <span className="astrik">*</span>
            </label>
            <Widgets.SelectCard image={articleData.coverImage} idType={"frontPassportId"} front setImage={handleImgDel("coverImage")} onImageUpload={handleImageUpload("articleImg")} />
          </div>
        </div>
      </div>
      <div className="article-body">
        <p className="art-form art-body-title">{t("artBody")}</p>
        <div className="art-form-body">
          {lang.value === "en" ? (
            <>
              {articleData?.en?.additionalContent &&
                articleData?.en?.additionalContent.sections.map((section, index) => (
                  <div key={section._id} className="article-contents">
                    <label className="art-label">{t("selContentEle")}</label>
                    <div className="trash-art">
                      <Widgets.NewSelectDropdown
                        options={listType}
                        selectedValue={
                          section.content && section.content.length > 0 ? { value: "unorderedListItem", label: t("unorderedListItem") } : { value: "description", label: t("description") }
                        }
                        onChange={(value) => handleDropdownChange(value, index)}
                        placeholder="Select content type"
                        big
                        noMargin
                        disabled={!section?.isAdded}
                      />
                      <img src={Icons.trashIcon} alt="trash" className="art-trash-icon" onClick={() => handleContentDelete(index)} />
                    </div>
                    <div className="art-content-category title-body">
                      <label className="art-label">{t("heading")} (En)</label>
                      <input
                        value={section.title}
                        maxLength={500}
                        className="art-input"
                        placeholder={t("entrHeading")}
                        name="content"
                        onChange={(e) => {
                          const newTitle = e.target.value.trimStart();
                          const langKey = lang.value;

                          setArticleData((prevArticleData) => {
                            const updatedSections = [...prevArticleData[langKey].additionalContent.sections];
                            updatedSections[index] = {
                              ...updatedSections[index],
                              title: newTitle,
                            };
                            return {
                              ...prevArticleData,
                              [langKey]: {
                                ...prevArticleData[langKey],
                                additionalContent: {
                                  ...prevArticleData[langKey].additionalContent,
                                  sections: updatedSections,
                                },
                              },
                            };
                          });
                        }}
                      />
                    </div>
                    {section.content && section.content.length > 0 ? (
                      <textarea
                        placeholder={t("entrListContent")}
                        value={section.content ? section.content.join("\n") : ""}
                        onChange={(e) => handleInputChange(e, index)}
                        className="textarea-article"
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/^\s+/, "").replace(/ {3,}/g, "  ");
                        }}
                      />
                    ) : (
                      <textarea
                        placeholder={t("entrDesciption")}
                        value={section.description || ""}
                        onChange={(e) => handleInputChange(e, index, "description")}
                        className="textarea-article"
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/^\s+/, "").replace(/ {3,}/g, "  ");
                        }}
                      />
                    )}
                    <hr className={`dotted-line-article ${index === articleData?.en?.additionalContent.sections.length - 1 ? "hide-border" : ""}`} />
                  </div>
                ))}
            </>
          ) : (
            <>
              {articleData?.vi?.additionalContent &&
                articleData?.vi?.additionalContent.sections.map((section, index) => (
                  <div key={section._id} className="article-contents">
                    <label className="art-label">{t("selContentEle")}</label>
                    <div className="trash-art">
                      <Widgets.NewSelectDropdown
                        options={listType}
                        selectedValue={
                          section.content && section.content.length > 0 ? { value: "unorderedListItem", label: t("unorderedListItem") } : { value: "description", label: t("description") }
                        }
                        onChange={(value) => handleDropdownChange(value, index)}
                        placeholder="Select content type"
                        big
                        noMargin
                        disabled={!section?.isAdded}
                      />
                      <img src={Icons.trashIcon} alt="trash" className="art-trash-icon" onClick={() => handleContentDelete(index)} />
                    </div>
                    <div className="art-content-category title-body">
                      <label className="art-label">{t("heading")} (Vi)</label>
                      <input
                        value={section.title}
                        className="art-input"
                        placeholder={t("entrHeading")}
                        name="content"
                        maxLength={500}
                        onChange={(e) => {
                          const newTitle = e.target.value.trimStart();
                          const langKey = lang.value;

                          setArticleData((prevArticleData) => {
                            const updatedSections = [...prevArticleData[langKey].additionalContent.sections];
                            updatedSections[index] = {
                              ...updatedSections[index],
                              title: newTitle,
                            };
                            return {
                              ...prevArticleData,
                              [langKey]: {
                                ...prevArticleData[langKey],
                                additionalContent: {
                                  ...prevArticleData[langKey].additionalContent,
                                  sections: updatedSections,
                                },
                              },
                            };
                          });
                        }}
                      />
                    </div>
                    {section.content && section.content.length > 0 ? (
                      <textarea
                        placeholder={t("entrListContent")}
                        value={section.content ? section.content.join("\n") : ""}
                        onChange={(e) => handleInputChange(e, index)}
                        className="textarea-article"
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/^\s+/, "").replace(/ {3,}/g, "  ");
                        }}
                      />
                    ) : (
                      <textarea
                        placeholder={t("entrDesciption")}
                        value={section.description || ""}
                        onChange={(e) => handleInputChange(e, index, "description")}
                        className="textarea-article"
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/^\s+/, "").replace(/ {3,}/g, "  ");
                        }}
                      />
                    )}
                    <hr className={`dotted-line-article ${index === articleData?.en?.additionalContent.sections.length - 1 ? "hide-border" : ""}`} />
                  </div>
                ))}
            </>
          )}
        </div>
        <div class="line-container">
          <hr class="dotted-line" />
          <img src={Icons.plusIcon} alt="+" className="plus-symbol" onClick={handleAddSection} />
        </div>
        <div className="save-speciality dp-price-footer article-save-box">
          <div className="speciality-cancel" onClick={handleCancel}>
            {t("cancel")}
          </div>
          <div className="speciality-save" onClick={handleSave}>
            {t("save")}
          </div>
        </div>
        <div></div>
      </div>
      <Widgets.ConfirmModal
        showModal={showConfirmModal}
        handleCancel={handleCancelDelete}
        handleConfirm={handleConfirmDelete}
        heading="Delete Content"
        description="Are you sure you want to delete this content?"
      />
    </div>
  );
};

export default EditArticle;
